// Concept demo — map zoom + view-mode controls as a THIN, persistent bar
// BENEATH the map, shown in a real PLACEMENT turn. Renders the REAL ReinforceUI
// component so the layout (map, placement box, and the landscape action-column
// split) is IDENTICAL to the game — we only inject the control bar + a
// showRegionBonuses flag and drive zoom/pan. Preview-only.
import { Stack, useRouter } from 'expo-router'
import React from 'react'
import { Pressable, Text, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ReinforceUI } from '../../../components/turn/ReinforceUI'
import { MapControlsBar, type MapViewMode } from '../../../components/map/MapControlsBar'
import { PlayerRosterStrip } from '../../../components/game/PlayerRosterPanel'
import { GameMenu } from '../../../components/game/GameMenu'
import { buildActiveGameViewProps } from '../../../preview/buildActiveGameViewProps'
import { findScenario } from '../../../preview/scenarios'
import { useOrientation } from '../../../hooks/useOrientation'
import { armDragHint } from '../../../lib/dragHint'
import {
  clampZoomScale,
  rescaleAboutCenter,
  initialPanForFocus,
  ZOOM_STEP,
  MIN_ZOOM_SCALE,
  MAX_ZOOM_SCALE,
  type PanOffset,
} from '../../../lib/mapZoom'

// A normal placement turn — the fixture is the viewer's turn in placement.
const DEMO = findScenario('card-bonus-only')

export default function MapControlsDemo() {
  const router = useRouter()
  const { isLandscape } = useOrientation()

  const base = React.useMemo(
    () => (DEMO ? buildActiveGameViewProps(DEMO.game, { viewerSlot: DEMO.viewerSlot }) : null),
    [],
  )
  const presence = React.useMemo(
    () => new Set((base?.game.players ?? []).map((p) => p.userId)),
    [base],
  )

  const vb = base?.gameState.map.viewBox
  // Default framing = fully zoomed OUT (whole map). Reset returns here.
  const [scale, setScale] = React.useState(MIN_ZOOM_SCALE)
  const [pan, setPan] = React.useState<PanOffset>(() =>
    vb ? initialPanForFocus(vb.x ?? 0, vb.y ?? 0, vb.width, vb.height, MIN_ZOOM_SCALE) : { tx: 0, ty: 0 },
  )
  const [mode, setMode] = React.useState<MapViewMode>('game')
  const [rosterExpanded, setRosterExpanded] = React.useState(false)

  // Arm the one-shot drag hint on mount so the demo shows it once too.
  React.useEffect(() => {
    armDragHint()
  }, [])

  if (!base || !vb) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <Stack.Screen options={{ headerShown: false }} />
        <Text>Demo fixture unavailable.</Text>
      </View>
    )
  }

  const zoomBy = (dir: 1 | -1) => {
    const next = clampZoomScale(scale + dir * ZOOM_STEP)
    if (next === scale) return
    setPan(rescaleAboutCenter(vb.x ?? 0, vb.y ?? 0, vb.width, vb.height, scale, next, pan))
    setScale(next)
  }
  const resetZoom = () => {
    setScale(MIN_ZOOM_SCALE)
    setPan(initialPanForFocus(vb.x ?? 0, vb.y ?? 0, vb.width, vb.height, MIN_ZOOM_SCALE))
  }

  // Cog + roster — the portrait top bar AND (via actionPanelHeader) the
  // landscape action-column header, exactly like the real game.
  const header = (
    <View className="flex-row items-center gap-2">
      <GameMenu
        gameId={base.game.id}
        canResign
        gameState={base.gameState}
        playerCount={base.game.players.length}
        trigger={(open) => (
          <TouchableOpacity
            onPress={open}
            accessibilityLabel="Open game menu"
            accessibilityRole="button"
            className="bg-paper border-2 border-olive rounded-full items-center justify-center"
            style={{ width: 38, height: 38, flexShrink: 0 }}
          >
            <Text className="text-ink text-base">⚙</Text>
          </TouchableOpacity>
        )}
      />
      <PlayerRosterStrip
        state={base.gameState}
        players={base.game.players}
        currentPlayerIndex={base.game.currentPlayerIndex}
        onExpand={() => setRosterExpanded(true)}
        expanded={rosterExpanded}
        orientation="portrait"
        presence={presence}
      />
    </View>
  )

  const controlsBar = (
    <MapControlsBar
      onReset={resetZoom}
      onZoomIn={() => zoomBy(1)}
      onZoomOut={() => zoomBy(-1)}
      canZoomIn={scale < MAX_ZOOM_SCALE - 1e-6}
      canZoomOut={scale > MIN_ZOOM_SCALE + 1e-6}
      mode={mode}
      onModeChange={setMode}
    />
  )

  return (
    // SafeAreaView (like the real game screen) auto-pads every inset — including
    // the notch/Dynamic Island on the left in landscape — so the action column
    // and map clear the cutout without any manual inset math.
    <SafeAreaView className="flex-1 bg-canvas">
      <Stack.Screen options={{ headerShown: false }} />

      {/* Portrait top bar. In landscape the same header moves into the action
          column via ReinforceUI's actionPanelHeader. */}
      {!isLandscape && (
        <View className="flex-row items-center gap-2 px-2 py-1.5">
          {header}
        </View>
      )}

      <View style={{ flex: 1 }}>
        <ReinforceUI
          gameId={base.game.id}
          state={base.gameState}
          myPlayerId={base.myPlayerId}
          slotByPlayerId={base.slotByPlayerId}
          gamePlayers={base.game.players}
          viewerUserId={base.viewerUserId}
          actionPanelHeader={header}
          mapControlsBar={controlsBar}
          showRegionBonuses={mode === 'regions'}
          zoomEnabled
          panOffset={pan}
          onPanChange={setPan}
          zoomScale={scale}
          onZoomScaleChange={setScale}
          viewerJoinedAt={base.viewerJoinedAt}
        />
      </View>

      <Pressable
        onPress={() => router.back()}
        accessibilityLabel="Back to scenario list"
        style={{
          // Offsets are within SafeAreaView's padded box, so no inset math.
          position: 'absolute',
          ...(isLandscape ? { bottom: 8, left: 8 } : { top: 52, left: 8 }),
          backgroundColor: 'rgba(0,0,0,0.55)',
          borderRadius: 16,
          paddingVertical: 5,
          paddingHorizontal: 12,
        }}
      >
        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>‹ Previews</Text>
      </Pressable>
    </SafeAreaView>
  )
}
